import { browser } from '$app/environment';
import type { Survey } from '$lib/core/Survey';

/**
 * Service to manage consent-gated survey initialization
 */
export class SurveyInitializer {
  private static instance: SurveyInitializer;
  private initialized = false;
  private pendingQueue: Array<() => void> = [];
  private hasConsent = false;

  static getInstance(): SurveyInitializer {
    if (!SurveyInitializer.instance) {
      SurveyInitializer.instance = new SurveyInitializer();
    }
    return SurveyInitializer.instance;
  }

  private constructor() {
    // Check if consent was previously given by looking for any moui_consent_ entries
    if (browser) {
      this.hasConsent = this.checkExistingConsent();
    }
  }

  /**
   * Check if user has previously given consent by looking for moui_consent_ entries
   */
  private checkExistingConsent(): boolean {
    if (!browser) return false;

    // First check if the cookie exists
    const cookieExists = document.cookie.split(';').some(c => c.trim().startsWith('moui_consent_token='));
    if (!cookieExists) {
      return false; // No cookie = no consent
    }

    // Then verify localStorage has matching consent data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('moui_consent_')) {
        try {
          const consentData = JSON.parse(localStorage.getItem(key) || '{}');
          if (consentData.timestamp && consentData.policyId) {
            return true; // Found valid consent record with cookie
          }
        } catch (e) {
          // Invalid consent data, continue checking
        }
      }
    }
    return false; // Cookie exists but no valid localStorage entry
  }

  /**
   * Grant consent and trigger any pending initialization
   * Note: Actual consent storage is handled by GdprConsentBanner
   */
  grantConsent(): void {
    this.hasConsent = true;
    
    // Execute all pending operations
    this.pendingQueue.forEach(fn => fn());
    this.pendingQueue = [];
  }

  /**
   * Revoke consent and clear any stored data
   */
  revokeConsent(): void {
    this.hasConsent = false;
    this.initialized = false;
    if (browser) {
      // Clear all moui_consent_ entries
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('moui_consent_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      // Clear all walku-related storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('walku:')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('walku:')) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }

  /**
   * Check if user has granted consent
   */
  getConsentStatus(): boolean {
    // Always check fresh from storage in case consent was granted in another tab/session
    if (browser) {
      this.hasConsent = this.checkExistingConsent();
    }
    return this.hasConsent;
  }

  /**
   * Initialize survey (requires consent)
   */
  async initializeSurvey(survey: Survey, environment: 'development' | 'production' = 'development'): Promise<void> {
    if (!this.hasConsent) {
      return new Promise<void>((resolve) => {
        this.pendingQueue.push(() => {
          this.performSurveyInitialization(survey, environment).then(resolve);
        });
      });
    }

    return this.performSurveyInitialization(survey, environment);
  }

  /**
   * Check token (requires consent)
   */
  async checkTokenWithConsent(): Promise<void> {
    if (!this.hasConsent) {
      return new Promise<void>((resolve) => {
        this.pendingQueue.push(() => {
          this.performTokenCheck().then(resolve);
        });
      });
    }

    return this.performTokenCheck();
  }

  /**
   * Perform the actual survey initialization
   */
  private async performSurveyInitialization(survey: Survey, environment: 'development' | 'production'): Promise<void> {
    if (this.initialized) return;

    try {
      // Use dynamic import to avoid circular dependencies
      const { SurveyController } = await import('$lib/controllers/surveyController');
      
      // Initialize survey with proper data
      await SurveyController.initializeSurveyFromRawData(survey);
      this.initialized = true;
      console.log('Survey initialization completed after consent');
    } catch (error) {
      console.error('Failed to initialize survey:', error);
      throw error;
    }
  }

  /**
   * Perform the actual token check
   */
  private async performTokenCheck(): Promise<void> {
    try {
      // Use dynamic import to avoid circular dependencies
      const { SurveyController } = await import('$lib/controllers/surveyController');
      await SurveyController.requestSecurityToken(true);
      console.log('Token check completed after consent');
    } catch (error) {
      console.error('Failed to check token:', error);
      throw error;
    }
  }

  /**
   * Check if survey is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Reset initialization state (for development/testing)
   */
  reset(): void {
    this.initialized = false;
    this.pendingQueue = [];
  }
}

// Export singleton instance
export const surveyInitializer = SurveyInitializer.getInstance();